import { t } from 'i18next'
import { FC } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { Block } from '@mui/icons-material'
import Lock from '@mui/icons-material/Lock'
import { Box, Grid, Typography } from '@mui/material'

import { GRTooltip } from '../../../../../../components/GRTooltip/GRTooltip'
import { TrendIndicator, TrendIndicatorType } from '../../../../../../components/TrendIndicator/TrendIndicator'
import { ValueIndicator, ValueType } from '../../../../../../components/ValueIndicator/ValueIndicator'
import { useRevenueAndDownloadsDataAccessCheck } from '../../../../hooks/unifiedNewsEntryHooks'
import { NewsEntryType } from '../../../../types/UnifiedNewsEntry'
import { computeRevenueDownloadsRatioChange, computeRevenueDownloadsRatioTrend, computeTrend } from '../../../../utils/revenueDownloadsTrend'
import { RevenueDownloadsChart, RankChart } from '../../Charts'
import { NewsCardSectionDivider } from '../../NewsCardSectionDivider/NewsCardSectionDivider'
import type { NewsCardContentSectionProps } from '../NewsCardContentSections'
import './NewsCardContentRevenueAndDownloadsSection.scss'

type NewsCardContentRevenueAndDownloadsSectionProps = NewsCardContentSectionProps & {
  entryType: NewsEntryType
}

/**
 * Renders an explicit "no data" indicator when the underlying calculation
 * cannot produce a value (e.g. R/D ratio change when exactly one period has
 * zero downloads — division by zero). Wrapped in a block-level span so the
 * icon appears below the trend percentage, matching the layout used by
 * `ValueIndicator` and the Update Impacts table (GR-1985).
 */
const UnavailableValue: FC = () => {
  const { t: translate } = useTranslation()
  return (
    <span style={{ display: 'block' }}>
      <GRTooltip content={translate('common:no_data_available')}>
        <Block data-testid="news-rd-value-unavailable" fontSize="small" color="disabled" />
      </GRTooltip>
    </span>
  )
}

export const NewsCardContentRevenueAndDownloadsSection: FC<NewsCardContentRevenueAndDownloadsSectionProps> = ({ newsEntry, entryType }) => {
  const hideRevenueAndDownloadsData = useRevenueAndDownloadsDataAccessCheck(entryType)

  const data = newsEntry.revenueDownloadsData

  // Compute trends and value changes locally (instead of relying on the
  // backend-provided percentage fields) so zero-period edge cases match the
  // agreed Update Impacts behavior — see UpdateImpactAggregates / GR-1985.
  const revenueTrend = data ? computeTrend(data.sum14AfterRevenue, data.sum14BeforeRevenue) : 0
  const downloadsTrend = data ? computeTrend(data.sum14AfterDownloads, data.sum14BeforeDownloads) : 0
  const revenueChange = data ? data.sum14AfterRevenue - data.sum14BeforeRevenue : 0
  const downloadsChange = data ? data.sum14AfterDownloads - data.sum14BeforeDownloads : 0
  const ratioTrend = data
    ? computeRevenueDownloadsRatioTrend(data.sum14AfterRevenue, data.sum14AfterDownloads, data.sum14BeforeRevenue, data.sum14BeforeDownloads)
    : 0
  const ratioChange = data
    ? computeRevenueDownloadsRatioChange(data.sum14AfterRevenue, data.sum14AfterDownloads, data.sum14BeforeRevenue, data.sum14BeforeDownloads)
    : 0

  return (
    <Box className="NewsCardContentRevenueAndDownloadsSection" mb={2}>
      <NewsCardSectionDivider></NewsCardSectionDivider>

      {data ? (
        <>
          <Typography variant="body2" textAlign="center" mb={2} className="NewsCardContentRevenueAndDownloadsSection__rev-dl-description">
            <Trans i18nKey="newsfeed:update_impact_revenue_download_entry_description" />
          </Typography>
          <Grid container className="NewsCardContentRevenueAndDownloadsSection__header" alignItems="flex-start" justifyContent="space-evenly" mb={2}>
            <Grid item>
              <Typography variant="h6">{t('common:revenue_text')}</Typography>
              {hideRevenueAndDownloadsData ? (
                <Lock />
              ) : (
                <>
                  <TrendIndicator value={revenueTrend} type={TrendIndicatorType.Percentage} maximumFractionDigits={2} sign direction />
                  <ValueIndicator value={revenueChange} type={ValueType.Currency} displayZero />
                </>
              )}
            </Grid>
            <Grid item>
              <Typography variant="h6">{t('common:downloads_text')}</Typography>
              {hideRevenueAndDownloadsData ? (
                <Lock />
              ) : (
                <>
                  <TrendIndicator value={downloadsTrend} type={TrendIndicatorType.Percentage} maximumFractionDigits={2} sign direction />
                  <ValueIndicator value={downloadsChange} type={ValueType.Number} displayZero />
                </>
              )}
            </Grid>
            <Grid item>
              <Typography variant="h6">{t('common:revenue_downloads_ratio_shorthand')}</Typography>
              {hideRevenueAndDownloadsData ? (
                <Lock />
              ) : (
                <>
                  <TrendIndicator value={ratioTrend} type={TrendIndicatorType.Percentage} maximumFractionDigits={2} sign direction />
                  {ratioChange === undefined ? (
                    <UnavailableValue />
                  ) : (
                    <ValueIndicator value={ratioChange} type={ValueType.Currency} maximumFractionDigits={3} displayZero />
                  )}
                </>
              )}
            </Grid>
          </Grid>
          <div className="NewsCardContentRevenueAndDownloadsSection__chart">
            <RevenueDownloadsChart newsEntry={newsEntry} locked={hideRevenueAndDownloadsData} />
          </div>
        </>
      ) : (
        <div className="NewsCardContentRevenueAndDownloadsSection__chart">
          <RankChart newsEntry={newsEntry} />
        </div>
      )}
    </Box>
  )
}
